// What a task DELIVERS, and the admission record a mint carries.
// See `../task.ts` for the file docblock.

import { z } from 'zod';
import type { RawTaskId } from '../../value-objects/ids';

/**
 * What a task DELIVERS (doc 04). Most work ends in a PR, but devops tasks —
 * deploys, server ops, migrations, investigations, incident debugging — don't:
 * they deliver a markdown report + artifacts into `task_run_events` and the
 * babysit watch (which waits on a PR) is skipped for them.
 */
export const OutcomeKind = {
  /** Opens a pull request — the default coding-task outcome; the babysit loop watches it. */
  Pr: 'pr',
  /** Delivers a markdown report + artifacts into run events (no PR) — an investigation / digest. */
  Report: 'report',
  /** Performs an ops action (deploy / migration / server op); evidence lands in run events, no PR. */
  OpsAction: 'ops_action',
  /**
   * A PLATFORM STAMP, never a declared lane (#3400): a `pr` task reached
   * `completed` holding no `pull_request_number`. Written by exactly one
   * statement — the completed hop in `transitionTask` (`@developerz/db`
   * `queries/tasks/lifecycle.ts`) — and undone by exactly one: `linkTaskToPr`
   * restores `'pr'` in the same statement that stamps the number, so a webhook
   * that loses the completion race still lands the row as an honest `pr`.
   *
   * NO PRODUCER MAY DECLARE IT. Triage and the ops verb narrow through
   * {@link DeclaredOutcomeKindSchema}; `applyReportOutcome` matches
   * `outcome_kind = 'pr'`. Distinct from `report` because relabelling the row
   * `report` releases its plan dependents (`upstreamGroupUnmergedSql` arm 2
   * matches `report` + `completed`) while the promised PR does not exist — the
   * exact direction the DAG gate exists to refuse.
   *
   * #3677 (open, filed 2026-09-11) is the issue that MEASURED this population —
   * 73 of 79 completed plan tasks claiming `outcome_kind = 'pr'` and carrying no
   * pull request — and it asks for the completed hop to REFUSE that settle
   * rather than stamp it. A live sibling, not the reference this docblock should
   * have carried: the hop corrects instead (`@developerz/db`
   * `queries/tasks/lifecycle.ts`, § CORRECTED, NOT REFUSED), because two
   * legitimate endings sit in the same population, and that disagreement is why
   * #3677 is still open.
   */
  PrUnfulfilled: 'pr_unfulfilled',
} as const;
export const OutcomeKindSchema = z.enum(OutcomeKind);

/**
 * The kinds a PRODUCER may state on a mint — {@link OutcomeKind} minus the
 * platform stamps. Triage parses an LLM's `outcome_kind` through THIS, not
 * `OutcomeKindSchema`: a stamp-valued PENDING row is a lie (`pr_unfulfilled`
 * says a completion hop already ran), and it would bypass the two claim gates
 * that spell "commits to a repo" as `outcome_kind = 'pr'` equality
 * (`repoNotOnboardedSql`, `repoServicesRequiredCase` — `@developerz/db`).
 */
export const DeclaredOutcomeKindSchema = z.enum([
  OutcomeKind.Pr,
  OutcomeKind.Report,
  OutcomeKind.OpsAction,
]);

/**
 * DOES A TASK OF THIS KIND EVER OPEN A PULL REQUEST? — total over
 * {@link OutcomeKind}, and the one input the plan-group DAG needs to stop
 * demanding a merge from a slice that was never going to produce one (#2949).
 *
 * A `Record`, not a `Set` of PR-bearing kinds and not an `=== OutcomeKind.Pr`
 * test, for the reason every other totality table here is one: a new outcome
 * kind fails the build until somebody says whether it opens a pull request. The
 * inline comparison is what let this fact stay implicit while two subsystems
 * disagreed about it — the claim gate demanding `plan_groups.status = 'merged'`
 * from a `report` slice, and `markPlanGroupMergedByPr` being unable to supply it.
 *
 * WHAT IT COST, measured on prod 2026-09-02: `plan_groups` sat at 820
 * `dispatched` against 32 `merged`, and 5 dispatched groups held a non-PR task (3
 * `report`, 2 `ops_action`), each permanently blocking everything sequenced behind
 * it. developerz-ai/wurk#470 is the worked example — an `investigate` slice
 * COMPLETED, stayed `dispatched` forever, and six minutes later the repair sweep
 * killed the implementation slice behind it. Triage decomposing "investigate, then
 * fix" is a normal and good shape; it was guaranteed to fail.
 */
export const OUTCOME_KIND_OPENS_PR: Record<OutcomeKind, boolean> = {
  [OutcomeKind.Pr]: true,
  /**
   * TRUE, and that is the whole fix (#3400). A `pr_unfulfilled` row PROMISED a
   * pull request and holds none: the plan DAG keeps parking its dependents
   * (`upstreamGroupUnmergedSql` arm 2 derives from the FALSE rows and so never
   * matches it), `planGroupDisposition` routes it to `NoPrLink` — which NAMES the
   * deadlock for the dead-plan-dependency sweep — and the fleet narration leaves
   * its Done rung unticked. The stamp is an honest failure, not a delivered
   * report; relabelling the row `report` instead is what released dependents
   * while no PR existed.
   */
  [OutcomeKind.PrUnfulfilled]: true,
  /** Delivers a markdown report into run events. There is no branch and no PR. */
  [OutcomeKind.Report]: false,
  /** Performs an ops action; evidence lands in run events. There is no branch and no PR. */
  [OutcomeKind.OpsAction]: false,
};
export type OutcomeKind = z.infer<typeof OutcomeKindSchema>;

/**
 * The ADMISSION RECORD a mint carries — the two fields that only mean anything
 * together, in ONE place so no producer can learn the rule and no other producer
 * miss it (arc 101 doc 08 step 4).
 *
 * TWO ARMS, and the second one is the point. `outcomeKind` is optional on an
 * ordinary mint — omitted, it is DERIVED from the envelope's origin
 * ({@link OUTCOME_KIND_FOR_ORIGIN}), which is right for the sixteen inlets whose
 * origin genuinely answers what they deliver. It is REQUIRED the moment
 * `rawTaskId` is present, because a triage-minted plan is exactly the case where
 * the origin does NOT answer: triage decides the lane — a coding run whose
 * definition of done is a pull request, or a devops/debugger run that may
 * legitimately end in a report and no code at all — and that decision is not
 * recoverable from where the ask came from.
 *
 * `apps/runner/src/modes/worker/devops/capabilities.ts` already wrote down what
 * the omitted field costs: a devops run that inherited the platform's
 * `outcome_kind ?? 'pr'` "would open a pull request on a customer's repository as
 * the silent consequence of an omitted field". Every `OUTCOME_KIND_FOR_ORIGIN`
 * row a triage plan would plausibly carry (`manual`, `gh_issue`, `sentry`,
 * `linear`, `conversation`) is `pr`, so the default is not merely unhelpful here
 * — it is wrong in exactly the dangerous direction, silently.
 *
 * A UNION RATHER THAN A DOCUMENTED CONVENTION. Stating it in the type is what
 * makes "triage always states the lane" checkable by the compiler instead of
 * remembered by the next producer — the same forcing function
 * {@link OUTCOME_KIND_FOR_ORIGIN}'s `Record<TaskOrigin, …>` is, applied to a pair
 * of fields rather than to one enum.
 *
 * THREE USERS, which is why it lives here and not in any one of them: the queue
 * placement a dispatch carries (`DispatchScheduling`), the `tasks` INSERT input
 * (`CreateTaskInput`, `@developerz/db`) and the sink port between them
 * (`DirectSinkStore`, `@developerz/agent`). A second spelling in any of the three
 * is how a router-minted task reaches `createTask` with a `rawTaskId` and no lane.
 */
export type TaskAdmission =
  | {
      rawTaskId?: undefined;
      /**
       * What the task delivers (doc 04). Omitted ⇒ DERIVED from the envelope's
       * origin, never left to the column default. Set it where the origin cannot
       * answer: the weekly gardener's box-maintenance task (#456) mints
       * `origin: manual` — ordinarily coding work — and delivers a `report`, and
       * the `ops` verb mints `origin: devops` with `ops_action`. An explicit
       * value always wins.
       */
      outcomeKind?: OutcomeKind;
    }
  | {
      /** The raw task triage fanned into this one-repo piece of work. */
      rawTaskId: RawTaskId;
      /** STATED, never inherited — see the type's docblock. */
      outcomeKind: OutcomeKind;
    };
